import { Loader2, Music2 } from "lucide-react";
import type { MusicTrack } from "../../api";
import { MusicPicker } from "../MusicPicker";
import { ctaGhostStyle } from "../styles";
import { CostBadge } from "./EditSectionRow";

/** Swap the background-music TRACK on a finished render. Sits beside the
 *  volume slider under Sound: same subject, same ffmpeg-only remix path,
 *  no re-render either way. */
export function MusicTrackCard({
  tracks,
  selected,
  onChange,
  onTracksChange,
  loadError,
  onApply,
  busy,
}: {
  tracks: MusicTrack[] | null;
  selected: string;
  onChange: (t: string) => void;
  onTracksChange: (t: MusicTrack[]) => void;
  loadError: string | null;
  onApply: () => void;
  busy: boolean;
}): React.JSX.Element {
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 14,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          Music track
        </span>
        <CostBadge>~5–20s</CostBadge>
      </div>
      <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12 }}>
        Swap the bed on this render. Re-mixes audio only — the picture, captions
        and timing are untouched.
      </div>
      <div style={{ marginBottom: 12, flex: 1 }}>
        <MusicPicker
          tracks={tracks}
          selected={selected}
          onChange={onChange}
          onTracksChange={onTracksChange}
          loadError={loadError}
        />
      </div>
      <button
        onClick={onApply}
        disabled={busy || !selected.trim()}
        style={{
          ...ctaGhostStyle,
          cursor: busy || !selected.trim() ? "not-allowed" : "pointer",
          opacity: busy || !selected.trim() ? 0.5 : 1,
        }}
      >
        {busy ? <Loader2 size={14} className="ql-spin" /> : <Music2 size={14} strokeWidth={2} />}
        Change music
      </button>
    </div>
  );
}

/**
 * Music-bed volume slider. Drives the audio-only re-mix path — fast
 * (~5–20s ffmpeg) so iteration is cheap. The slider's position reflects
 * the volume actually baked into the latest render (read from
 * `music.json` on mount); the Apply button stays disabled until the
 * slider's been moved at least 0.005 away from the applied value, so
 * the operator can't accidentally trigger a redundant re-mix.
 */
export function MusicVolumeCard({
  value,
  onChange,
  applied,
  defaultValue,
  min,
  max,
  onApply,
  busy,
}: {
  value: number;
  onChange: (v: number) => void;
  applied: number;
  defaultValue: number;
  min: number;
  max: number;
  onApply: () => void;
  busy: boolean;
}): React.JSX.Element {
  const dirty = Math.abs(value - applied) > 0.005;
  const pct = (n: number): string => `${(((n - min) / (max - min)) * 100).toFixed(1)}%`;
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 14,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          Music volume
        </span>
        <CostBadge>~10s</CostBadge>
      </div>
      <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 14 }}>
        Adjust the background music level. Re-mixes audio only — no re-render.
      </div>

      <div style={{ position: "relative", marginBottom: 6, display: "flex", alignItems: "center", height: 20 }}>
        <input
          type="range"
          className="ql-range"
          min={min}
          max={max}
          step={0.005}
          value={value}
          onChange={(e) => onChange(Number.parseFloat(e.target.value))}
          disabled={busy}
          aria-label="Music volume"
          style={{
            cursor: busy ? "not-allowed" : "pointer",
            // Track fill up to the current value — the .ql-range class owns
            // the geometry; only the live percentage must be inline.
            background: `linear-gradient(to right, rgba(255,213,0,.75) ${pct(value)}, rgba(255,255,255,.10) ${pct(value)})`,
          }}
        />
        {/* Default-position marker. A hairline tick at the default volume so
            the operator can always feel where "house default" is. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: pct(defaultValue),
            top: 2,
            bottom: 2,
            width: 1,
            background: "rgba(255,213,0,.55)",
            pointerEvents: "none",
            transform: "translateX(-0.5px)",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          fontFamily: "var(--ql-font-mono)",
          color: "var(--ql-fg-subtle)",
          marginBottom: 12,
        }}
      >
        <span>quiet</span>
        <span style={{ color: "var(--ql-yellow)", fontWeight: 700 }}>
          {value.toFixed(3)}
        </span>
        <span>loud</span>
      </div>
      <button
        onClick={onApply}
        disabled={busy || !dirty}
        title={
          dirty
            ? `Re-mix at ${value.toFixed(3)} (was ${applied.toFixed(3)})`
            : "Slider matches the current render — move it to enable Apply"
        }
        style={{
          ...ctaGhostStyle,
          marginTop: "auto",
          cursor: busy || !dirty ? "not-allowed" : "pointer",
          opacity: busy || !dirty ? 0.5 : 1,
        }}
      >
        {busy ? <Loader2 size={14} className="ql-spin" /> : <Music2 size={14} strokeWidth={2} />}
        {dirty ? "Apply music level" : "Music level applied"}
      </button>
    </div>
  );
}
